import Button from '@/src/common/components/marketing/buttons/Button'
import { startsWithAVowel } from '@/src/modules/pg-plus/redemption/utilities'
import { styled } from '@/src/stitches.config'
import { TEGiftQueryResult } from '@/src/common/queries/PostGiftRedeemEGift'
import usePostRedeemEgift from '@/src/common/hooks/usePostRedeemEgift'
import { UseMutationResult } from 'react-query'
import { GIFT_DETAILS_STEP } from '@/src/modules/pg-plus/redemption/constants'
import { usePGPlusStepAndModalContext } from '@/src/common/contexts/PGPlusModalAndStepContext'
import {
    DISPATCH_HIDE_MODAL,
    DISPATCH_SET_STEP,
} from '@/src/common/reducers/constants'
import DelayForEGiftAlert from '@/src/common/components/marketing/alerts/24HourDelayForEGiftAlert'
import { ModalButtonsWrapper } from '@/src/common/components/elements/Modals/StyledModalComponents'
import { BRANDED_EGIFT, FAQ_PAGE } from '@/src/modules/pg-plus/constants'
import { scrollToTop } from '@/src/common/utilities/scrollToTop'

const ErrorMessage = styled('p', {
    fontWeight: 'bold',
    color: '$error',
    '& a': {
        color: '$secondary',
        textDecoration: 'underline',
    },
})

interface IDetails {
    errors: any[]
    setErrors: Function
    refetchGift: Function
}

const EGiftModalContent = ({ errors, setErrors, refetchGift }: IDetails) => {
    const { dispatch, giftState, setSelection } = usePGPlusStepAndModalContext()
    const { cardUuid, brand, is24HoursOld } = giftState
    const startsWithVowel = startsWithAVowel(brand)
    const hasErrors = !!errors.length
    const {
        mutate: postRedeem,
        isLoading: redeemIsLoading,
        isSuccess: redeemIsSuccess,
    }: UseMutationResult<TEGiftQueryResult> = usePostRedeemEgift()

    return (
        <>
            {!is24HoursOld && <DelayForEGiftAlert />}
            <p data-testid={'modal-header'} className={'header'}>
                Get {startsWithVowel ? 'an' : 'a'} {brand}{' '}
                <span className={'gradient'}>eGift</span>
            </p>
            <p>Works like a regular gift card.</p>
            <p>
                You&apos;ll receive the eGift Card within 24 hours of making
                your redemption selection.
            </p>
            <p>Use it in store or online.</p>
            {hasErrors && (
                <ErrorMessage className={'error'}>
                    Error: {errors}. Please try again. If issues persist, see
                    our list of{' '}
                    <a href={FAQ_PAGE}>frequently asked questions</a>.
                </ErrorMessage>
            )}
            <ModalButtonsWrapper marginTop={'none'} gap={'5px'}>
                <Button
                    label={'Confirm'}
                    variant={'primary'}
                    width={'standard'}
                    showArrow={true}
                    textTransform={'uppercase'}
                    onClick={() => {
                        try {
                            postRedeem(
                                { card_uuid: cardUuid },
                                {
                                    onSuccess: () => {
                                        setSelection(BRANDED_EGIFT)
                                        setTimeout(() => {
                                            refetchGift()
                                            dispatch({
                                                type: DISPATCH_SET_STEP,
                                                payload: {
                                                    stepName: GIFT_DETAILS_STEP,
                                                },
                                            })
                                            scrollToTop('__next')
                                        }, 3000)
                                    },
                                    onError: (error) => {
                                        setErrors(error)
                                        console.error(error)
                                    },
                                }
                            )
                        } catch (error: any) {
                            setErrors(error)
                            console.error(error)
                        }
                    }}
                    isLoading={redeemIsLoading || redeemIsSuccess}
                    disabled={redeemIsLoading || redeemIsSuccess}
                    data-testid={'exchange-button'}
                />
                <Button
                    label={'Go back'}
                    variant={'primaryOutline'}
                    width={'standard'}
                    showArrow={true}
                    textTransform={'uppercase'}
                    disabled={redeemIsLoading || redeemIsSuccess}
                    data-testid={'back-button'}
                    onClick={() => {
                        dispatch({
                            type: DISPATCH_HIDE_MODAL,
                        })
                    }}
                />
            </ModalButtonsWrapper>
        </>
    )
}

export default EGiftModalContent
